import './generic-edit-screen.scss'

import { defineComponent, markRaw, Component, PropType } from 'vue';
import { take } from 'rxjs/operators'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'

import { EntityField, EntityFieldStatus, MediaUploadResult2, RecordMaskResource, MetaRecordMaskResource, getTranslatedString } from '@skinsoft/types';
import { EditScreenViewControllerInterface } from '@skinsoft/classes-interfaces-definition';
import { ImageTransferHelper } from '@skinsoft/media-transfer-helper';

import { reactiveVueMixin } from "@skinsoft/reactive-vue";
import { DELETE_IMAGE_CLICK_TOPIC, DISPLAY_ALERT_TOPIC, SAVE_GENERIC_EDIT_SCREEN } from '@skinsoft/bus';
import { AddMediaEvent, DeleteImageClickEvent } from '@skinsoft/entity-ui';
import { MaskFieldUpdateEvent } from '@skinsoft/record-mask/src/components/mask-field';
import { RecordMaskDisplay } from '@skinsoft/record-mask';
import { SELECTED_TAB_CHANGE_EVENT, TabsMenu, TabsMenuEntry } from '@skinsoft/ui/src/tabs-menu';
import { FailedImageUpload, UploadImageFailureDialog } from '@skinsoft/ui2/error-handling/upload-image-failure-dialog';
import { CANCEL_ALL_IMAGE_UPLOAD, CANCEL_IMAGE_UPLOAD, RETRY_ALL_IMAGE_UPLOAD, RETRY_IMAGE_UPLOAD } from '@skinsoft/ui2';
import { GENERIC_EDIT_SCREEN_MEDIA_ADDED } from '@skinsoft/events';

export interface DisabledMaskFields {
    entityId: string
    message?: string
}

export interface GenericEditScreenSection {
    id: string,
    component: Component,
    sectionTitle?: string,
    props: any,
    eventListeners: any,
    dataCy?: string,
}

export const GenericEditScreen = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sRecordMaskDisplay: RecordMaskDisplay,
        sTabsMenu: TabsMenu,
        sUploadImageFailureDialog: UploadImageFailureDialog,
    },
    props: {
        id: {
            type: String,
        },
        recordMasks: {
            type: Array as PropType<(RecordMaskResource | MetaRecordMaskResource)[]>,
            required: true,
        },
        viewController: {
            type: Object as PropType<EditScreenViewControllerInterface<any>>,
            required: true,
        },
        headerTitle: {
            type: String,
        },
        additionalSections: {
            type: Array as PropType<GenericEditScreenSection[]>,
            default: [],
        },
        editing: {
            type: Boolean,
        },
        saveSuccessMessageKey: {
            type: String,
            default: "genericEditScreen.saveSuccessMsg",
        },
        saveErrorMessageKey: {
            type: String,
            default: "genericEditScreen.saveErrorMsg",
        },
        genericErrorMessageKey: {
            type: String,
            default: "genericEditScreen.genericErrorMsg",
        },
        failedToLoadEntityErrorMsgKey: {
            type: String,
            default: "genericEditScreen.genericErrorMsg",
        },
        navigateBackRoute: {
            type: String
        },
        disabledMaskFields: {
            type: Array as PropType<DisabledMaskFields[]>,
            default: () => [],
        },
        contentHidden: {
            type: Boolean,
            default: false,
        },
        fieldsErrors: {
            type: Array as PropType<{ entityId: string, message: string }[]>,
            default: () => [],
        },
    },
    template: require('./generic-edit-screen.html'),
    data() {
        return {
            entity: null as any | null,
            isLoading: false,
            failedImageUploads: [] as FailedImageUpload[],

            isOnline: window.navigator.onLine,

            sections: [] as GenericEditScreenSection[],
            displayedSectionIndex: 0,

            backIcon: faArrowLeft,

            SELECTED_TAB_CHANGE_EVENT,
            RETRY_ALL_IMAGE_UPLOAD,
            CANCEL_ALL_IMAGE_UPLOAD,
            RETRY_IMAGE_UPLOAD,
            CANCEL_IMAGE_UPLOAD,
            // DELETE_IMAGE_CLICK_TOPIC isn't exposed on `this`: only the imported constant is used,
            // directly below in initSection().
        }
    },
    computed: {
        tabsEntries(): TabsMenuEntry[] {
            return this.sections.map(section => ({
                title: section.sectionTitle ?? '',
                dataCy: section.dataCy,
            }))
        },
    },
    methods: {
        initSection() {
            const showMediaSection = this.recordMasks.length === 1
            for(const recordMask of this.recordMasks) {
                this.sections.push({
                    id: recordMask.id ?? '',
                    // markRaw: without it, this component definition would be deep-proxied into
                    // reactive data — no benefit (it's never mutated), and exactly the overhead Vue
                    // warns about.
                    component: markRaw(RecordMaskDisplay),
                    sectionTitle: recordMask.name ? getTranslatedString(recordMask.name, this.$i18n.locale) : undefined,
                    props: () => {
                        return {
                            recordMask: recordMask,
                            entity: this.entity,
                            isMetaMask: false,
                            showMediaSection: showMediaSection,
                            editing: this.editing,
                            getEntityFieldStatus: this.getEntityFieldStatus,
                            forceMediaUploadInCache: true,
                            class:"edit-screen-mask-display"
                        }
                    },
                    eventListeners: () => {
                        return {
                            "value-change": this.onValueChange,
                            "addMedia": this.onAddMedia,
                            "imageChoice": this.onAddFirstMedia,
                            DELETE_IMAGE_CLICK_TOPIC: this.onImageDeleteClick,
                        }
                    },
                    dataCy: recordMask.dataCy ?? undefined,
                })
            }

            this.sections = this.sections.concat(this.additionalSections)
        },
        fetchEntity() {
            if(this.id) {
                this.isLoading = true
                this.viewController.getEntity(this.id).subscribe(
                    entity => {
                        this.entity = entity
                        this.isLoading = false
                    },

                    error => {
                        const alertPayload = {
                            message: this.$t(this.failedToLoadEntityErrorMsgKey),
                            variant: "danger"
                        }
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                        this.isLoading = false
                    }
                )
            } else {
                const alertPayload = {
                    message: this.$t(this.genericErrorMessageKey),
                    variant: "danger"
                }
                this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
            }
        },
        save() {
            if(this.entity) {
                this.viewController.saveEntity(this.entity, this.recordMasks).subscribe(
                    async saveResult => {
                        if(saveResult.success) {
                            const alertPayload = {
                                message: this.$t(this.saveSuccessMessageKey),
                                variant: "success"
                            }
                            this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                        } else {
                            if(saveResult.mediaUploadResult.length) {
                                this.handleFailedImageUpload(saveResult.mediaUploadResult)
                            }

                            const alertPayload = {
                                message: this.$t(this.saveErrorMessageKey),
                                variant: "danger"
                            }
                            this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                        }
                    },

                    error => {
                        const alertPayload = {
                            message: this.$t(this.saveErrorMessageKey),
                            variant: "danger"
                        }
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                    },
                )
            } else {
                const alertPayload = {
                    message: this.$t(this.genericErrorMessageKey),
                    variant: "danger"
                }
                this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
            }
        },
        async handleFailedImageUpload(mediasUploadResults: MediaUploadResult2[]) {
            this.failedImageUploads = []
            const imageTransferHelper = ImageTransferHelper.getInstance()

            for(const mediaUploadResult of mediasUploadResults) {
                if(mediaUploadResult.timeOut) {

                }
                else if(mediaUploadResult.errorOccured) {
                    if(mediaUploadResult.cacheKey) {
                        try {
                            const base64 = await imageTransferHelper
                                .getCachedMediaValueAsBase64(mediaUploadResult.cacheKey).pipe(
                                    take(1), // needed for promise to resolve (the observable must complete)
                                )
                                .toPromise()


                            this.failedImageUploads.push({
                                cacheKey: mediaUploadResult.cacheKey,
                                mediaUrl: base64,
                            })
                        } catch(err) {
                        }
                    }
                }
            }

            if(this.failedImageUploads.length) {
                this.openImageUploadFailureDialog()
            }
        },
        openImageUploadFailureDialog() {
            (this.$refs.uploadImageFailureDialog as InstanceType<typeof UploadImageFailureDialog>).openModal()
        },
        closeImageUploadFailureDialog() {
            (this.$refs.uploadImageFailureDialog as InstanceType<typeof UploadImageFailureDialog>).closeModal()
        },
        onValueChange(event: MaskFieldUpdateEvent) {
            if(this.entity) {
                this.viewController.updateEntity(event, this.entity)
            }
        },
        onAddMedia(addedMedia: AddMediaEvent) {
            if(this.entity) {
                this.viewController.addImage(addedMedia.cacheKey, this.entity)
                this.$emit(GENERIC_EDIT_SCREEN_MEDIA_ADDED, addedMedia)
            } else {
                const alertPayload = {
                    message: this.$t(this.genericErrorMessageKey),
                    variant: "danger"
                }
                this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
            }
        },
        onImageDeleteClick(event: DeleteImageClickEvent) {
            if(this.entity && event.imageId) {
                this.viewController.deleteImage(event.imageId, this.entity)
            } else {
                const alertPayload = {
                    message: this.$t(this.genericErrorMessageKey),
                    variant: "danger"
                }
                this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
            }
        },
        onAddFirstMedia(mediaId: string) {
            if(this.entity && mediaId) {
                this.viewController.setFirstMedia(mediaId, this.entity)
            } else {
                const alertPayload = {
                    message: this.$t(this.genericErrorMessageKey),
                    variant: "danger"
                }
                this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
            }
        },
        onRetryUploadAllImages() {
            this.save()
            this.closeImageUploadFailureDialog()
        },
        onCancelUploadAllImages() {
            for(let i=this.failedImageUploads.length-1; i>=0; i--) {
                this.onCancelUploadImage(i)
            }
            this.closeImageUploadFailureDialog()
        },
        onRetryUploadImage(index: number) {
            const failedUpload = this.failedImageUploads[index]
            this.failedImageUploads[index] = {...failedUpload, currentlyUploading: true} as FailedImageUpload
            this.viewController.uploadImage(this.entity, failedUpload.cacheKey).subscribe(
                uploadResult => {
                    if(uploadResult.mediaId && uploadResult.cacheKey) {
                        const failedUploadIndex = this.failedImageUploads.findIndex(failedImageUpload => {
                            return failedImageUpload.cacheKey === uploadResult.cacheKey
                        })

                        this.failedImageUploads.splice(failedUploadIndex, 1)
                    } else {
                        this.failedImageUploads[index] = {...failedUpload, currentlyUploading: false} as FailedImageUpload
                    }
                },

                err => {
                    this.failedImageUploads[index] = {...failedUpload, currentlyUploading: false} as FailedImageUpload
                }
            )
        },
        onCancelUploadImage(index: number) {
            const failedUpload = this.failedImageUploads[index]
            this.viewController.deleteImage(failedUpload.cacheKey, this.entity)
            this.failedImageUploads.splice(index, 1)
        },
        getEntityFieldStatus(entityField: EntityField): EntityFieldStatus {
            const result: EntityFieldStatus = {
                disabled: false,
                error: false
            }

            if(!this.isOnline) {
                // entityFieldId is used over id: id isn't always present on entityField, entityFieldId always is
                const disabledEntityField = this.disabledMaskFields?.find(disabledField => {
                    return disabledField.entityId === (entityField as any).entityFieldId
                })

                if(disabledEntityField) {
                    result.disabled = true
                    if(disabledEntityField.message) {
                        result.disabledMessage = disabledEntityField.message
                    }
                }
            }

            const errorEntityField = this.fieldsErrors?.find(error => {
                return error.entityId === (entityField as any).entityFieldId
            })

            if(errorEntityField) {
                result.error = true
                result.errorMessage = errorEntityField.message
            }

            return result
        },
        updateConnexionStatus() {
            this.isOnline = window.navigator.onLine
        },
        closeEditScreen() {
            this.$router.push({name: this.navigateBackRoute})
        },
        isHiddenSection(index) {
            return index !== this.displayedSectionIndex
        },
        onSelectedTabChanged(index: number) {
            this.displayedSectionIndex = index
        },
    },
    watch: {
        id: {
            handler() {
                this.fetchEntity()
            },
            immediate: true,
        },
    },
    mounted() {
        this.fetchEntity()

        this.subscribeToBus(SAVE_GENERIC_EDIT_SCREEN, this.save)

        // navigator.onLine is just a snapshot; the online/offline events are what keep isOnline in sync
        window.addEventListener('online', this.updateConnexionStatus)
        window.addEventListener('offline', this.updateConnexionStatus)

        this.initSection()
    },
})
